import * as THREE from "three";
import { SPILL_AT, type Drink, type Glass } from "../game/food";

// The drink in a soda cup: a column of liquid that rises as it's poured, in
// the drink's colour, with a white fizz on top, and when it's poured past
// the brim, foam over the top and drips down the outside.

const COLORS: Record<Drink, { color: number; opacity: number }> = {
  water: { color: 0xbfe6f5, opacity: 0.55 },
  pop: { color: 0x2a1006, opacity: 1 },
  diet: { color: 0x6b3a1a, opacity: 1 },
  lemon: { color: 0xd8e05a, opacity: 0.85 },
};

/** The cup's inside, from the bottom (`dishes.ts`'s tumbler lathe). */
const BOTTOM = 0.004;
const HEIGHT = 0.134;

// Just inside the cup's narrowest point, so a part-full column never pokes
// through the tapering wall (the column is stretched to the level, not cut).
const R_BOTTOM = 0.026;
const R_TOP = 0.03;
const liquidGeo = new THREE.CylinderGeometry(R_TOP, R_BOTTOM, 1, 18, 1, true);
liquidGeo.translate(0, 0.5, 0);
const topGeo = new THREE.CircleGeometry(1, 18);
topGeo.rotateX(-Math.PI / 2);
const dripGeo = new THREE.CylinderGeometry(0.004, 0.004, 1, 6);
dripGeo.translate(0, -0.5, 0);

export class CupFill {
  readonly group = new THREE.Group();
  private readonly liquid: THREE.Mesh;
  private readonly surface: THREE.Mesh;
  private readonly foam: THREE.Mesh;
  private readonly drips: THREE.Mesh[] = [];
  private readonly mat = new THREE.MeshStandardMaterial({ transparent: true, roughness: 0.6, envMapIntensity: 0.2 });

  constructor() {
    this.liquid = new THREE.Mesh(liquidGeo, this.mat);
    this.liquid.position.y = BOTTOM;
    // Drawn before the cup around it, which blends over it.
    this.liquid.renderOrder = 1;
    this.surface = new THREE.Mesh(topGeo, this.mat);
    this.surface.renderOrder = 1;
    this.foam = new THREE.Mesh(new THREE.SphereGeometry(0.041, 14, 6, 0, Math.PI * 2, 0, Math.PI / 2), new THREE.MeshStandardMaterial({ color: 0xfaf6ee, roughness: 0.9 }));
    this.foam.scale.y = 0.35;
    this.foam.position.y = BOTTOM + HEIGHT;
    this.group.add(this.liquid, this.surface, this.foam);
    for (const a of [0.4, 2.3, 4.1]) {
      const drip = new THREE.Mesh(dripGeo, this.mat);
      drip.position.set(Math.cos(a) * 0.041, BOTTOM + HEIGHT, Math.sin(a) * 0.041);
      this.drips.push(drip);
      this.group.add(drip);
    }
  }

  set(glass: Glass): void {
    const shown = !!glass.drink && glass.fill > 0;
    this.group.visible = shown;
    if (!shown) return;
    const { color, opacity } = COLORS[glass.drink!];
    this.mat.color.setHex(color);
    this.mat.opacity = opacity;
    const level = Math.min(glass.fill, SPILL_AT) * HEIGHT;
    this.liquid.scale.y = Math.max(0.001, level);
    this.surface.position.y = BOTTOM + level;
    this.surface.scale.setScalar(R_TOP);
    const over = glass.fill > SPILL_AT;
    this.foam.visible = over;
    this.drips.forEach((d, i) => {
      d.visible = over;
      d.scale.y = Math.min(0.12, (glass.fill - SPILL_AT) * (0.6 + i * 0.25));
    });
  }
}
